import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import {
  Card,
  Row,
  Col,
  Form,
  Button,
  Spinner,
  Table,
  Badge,
  ListGroup,
  Alert
} from 'react-bootstrap';
import { Link } from 'react-router-dom';
import receptionService from '../../../services/receptionService';
import medicalDossierService from '../../../services/medicalDossierService';
import VisitHistoryTableRows from '../../receptionist/panels/VisitHistoryTableRows';
import { useDoctorWorkspace } from '../doctorWorkspaceContext';
import { DOCTOR_COPY } from '../doctorCopy';
import { doctorConsultationEditPath } from '../doctorPaths';
import { filterDossierForWorkspace } from '../utils/dossierWorkspaceFilter';
import { HeadDoctorPageHeader } from '../../head-doctor/ui';
import { HEAD_DOCTOR_COPY } from '../../head-doctor/headDoctorCopy';
import Icon from '../../common/Icons';
import './DoctorDossierPage.css';

const D = DOCTOR_COPY.DOSSIER;

const SERVICE_LINE_BADGE = {
  MEDICAL: { label: 'Médecine', bg: 'primary' },
  DENTAL: { label: 'Dentiste', bg: 'info' },
  NURSING: { label: 'Infirmerie', bg: 'secondary' }
};

const RDV_STATUS_LABEL = {
  EN_ATTENTE: 'En attente',
  CONFIRME: 'Confirmé',
  TERMINE: 'Terminé',
  ANNULE: 'Annulé',
  ABSENT: 'Absent'
};

function computeAgeYears(dateNaissance) {
  if (!dateNaissance || String(dateNaissance).startsWith('0000')) return null;
  const d = new Date(dateNaissance);
  if (Number.isNaN(d.getTime())) return null;
  const diff = Date.now() - d.getTime();
  return Math.floor(diff / (365.25 * 24 * 3600 * 1000));
}

function formatWhen(iso) {
  if (!iso) return '—';
  try {
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return String(iso);
    return d.toLocaleString('fr-FR', { dateStyle: 'short', timeStyle: 'short' });
  } catch {
    return String(iso);
  }
}

function vitalsSummary(c) {
  const bits = [];
  if (c.weight_kg != null && c.weight_kg !== '') bits.push(`${c.weight_kg} kg`);
  if (c.height_cm != null && c.height_cm !== '') bits.push(`${c.height_cm} cm`);
  if (c.bp_systolic != null && c.bp_diastolic != null) {
    bits.push(`TA ${c.bp_systolic}/${c.bp_diastolic}`);
  }
  if (c.temperature_c != null && c.temperature_c !== '') bits.push(`${c.temperature_c} °C`);
  if (c.heart_rate_bpm != null && c.heart_rate_bpm !== '') bits.push(`FC ${c.heart_rate_bpm}`);
  return bits.length ? bits.join(' · ') : '—';
}

function excerpt(text, max = 140) {
  const s = text != null ? String(text).trim() : '';
  if (!s) return { text: '', more: false };
  return { text: s.slice(0, max), more: s.length > max };
}

const VISIT_LIST_HEAD = (
  <div className="recv-journal-list__head" aria-hidden="true">
    <span className="recv-journal-list__cell recv-journal-list__cell--time">Passage</span>
    <span className="recv-journal-list__cell recv-journal-list__cell--patient">Patient</span>
    <span className="recv-journal-list__cell recv-journal-list__cell--site">Site</span>
    <span className="recv-journal-list__cell recv-journal-list__cell--consult">Consultation</span>
    <span className="recv-journal-list__cell recv-journal-list__cell--staff">Accueil</span>
  </div>
);

function buildChronology(d) {
  if (!d) return [];
  const out = [];
  const push = (atIso, key, payload) => {
    const t = atIso ? new Date(atIso).getTime() : 0;
    if (Number.isNaN(t)) return;
    out.push({ key, t, at: atIso, ...payload });
  };

  for (const c of d.consultations || []) {
    push(c.consultation_date, `c-${c.id}`, {
      kind: 'consultation',
      title: `Consultation · ${(c.service_line || 'MEDICAL').toUpperCase()}`,
      subtitle: c.site_name || '',
      body: c.symptoms || c.diagnosis || c.notes || ''
    });
  }
  for (const v of d.visits || []) {
    push(v.registered_at, `v-${v.id}`, {
      kind: 'visite',
      title: 'Passage accueil',
      subtitle: v.site_name || '',
      body: v.notes || v.service_line || ''
    });
  }
  for (const p of d.prescriptions || []) {
    push(p.prescribed_date, `p-${p.id}`, {
      kind: 'ordonnance',
      title: 'Ordonnance médicale',
      subtitle: p.doctor_name || '',
      body: (p.items || []).map((i) => i.medication_name || i.name || 'Médicament').join(', ')
    });
  }
  for (const r of d.rendezVous || []) {
    push(r.start_at, `r-${r.id}`, {
      kind: 'rdv',
      title: `Rendez-vous (${r.type_rendez_vous || '—'})`,
      subtitle: r.reference || '',
      body: r.motif || r.service || ''
    });
  }
  for (const nv of d.nursing?.vitals || []) {
    push(nv.created_at, `nv-${nv.id}`, {
      kind: 'soin',
      title: 'Constantes infirmières',
      subtitle: nv.consultation_id ? `Consultation #${nv.consultation_id}` : '',
      body: vitalsSummary(nv)
    });
  }
  for (const na of d.nursing?.careActions || []) {
    push(na.created_at, `na-${na.id}`, {
      kind: 'soin',
      title: `Soin · ${na.action_type || ''}`,
      subtitle: na.consultation_id ? `Consultation #${na.consultation_id}` : '',
      body: na.description || ''
    });
  }
  for (const nt of d.nursing?.transfers || []) {
    push(nt.created_at, `nt-${nt.id}`, {
      kind: 'transfert',
      title: `Transfert ${nt.from_service_line} → ${nt.to_service_line}`,
      subtitle: nt.consultation_id ? `Consultation #${nt.consultation_id}` : '',
      body: nt.reason || ''
    });
  }

  out.sort((a, b) => b.t - a.t);
  return out.slice(0, 100);
}

const CHRONO_BADGE = {
  consultation: 'primary',
  visite: 'info',
  ordonnance: 'warning',
  rdv: 'dark',
  soin: 'secondary',
  transfert: 'danger'
};

function latestVitalsSnapshot(d) {
  if (!d) return null;
  const vitalsList = [...(d.nursing?.vitals || [])].sort(
    (a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime()
  );
  const v0 = vitalsList[0];
  if (v0) {
    return {
      source: 'Infirmerie',
      at: v0.created_at,
      text: vitalsSummary(v0),
      note: v0.note || null
    };
  }
  const consults = [...(d.consultations || [])].sort(
    (a, b) => new Date(b.consultation_date).getTime() - new Date(a.consultation_date).getTime()
  );
  for (const c of consults) {
    const vs = vitalsSummary(c);
    if (vs !== '—') {
      return { source: 'Consultation', at: c.consultation_date, text: vs, note: null };
    }
  }
  return null;
}

export default function DoctorDossierPage() {
  const { toast, portalPaths, clinicalWorkspace } = useDoctorWorkspace();
  const isDentist = clinicalWorkspace === 'dentist';
  const isHeadDoctor = clinicalWorkspace === 'headDoctor';

  const [searchQuery, setSearchQuery] = useState('');
  const [searchHits, setSearchHits] = useState([]);
  const [searchLoading, setSearchLoading] = useState(false);
  const [pickListTitle, setPickListTitle] = useState('');
  const searchSeq = useRef(0);

  const [member, setMember] = useState(null);
  const [fullDossier, setFullDossier] = useState(null);
  const [dossierLoading, setDossierLoading] = useState(false);

  const loadDossier = useCallback(
    async (m) => {
      if (!m?.id || !String(m.matricule || '').trim()) return;
      setDossierLoading(true);
      setFullDossier(null);
      try {
        const res = await medicalDossierService.getMemberDossier(m.id, {
          consultLimit: 250,
          visitLimit: 120,
          rxLimit: 100,
          rdvLimit: 80
        });
        if (res.success && res.data) {
          setFullDossier(res.data);
        } else {
          toast('warning', res.message || 'Dossier incomplet');
          setFullDossier(null);
        }
      } catch (e) {
        toast('danger', e.message || D.ERROR_LOAD);
        setFullDossier(null);
      } finally {
        setDossierLoading(false);
      }
    },
    [toast]
  );

  useEffect(() => {
    if (member) loadDossier(member);
    else setFullDossier(null);
  }, [member, loadDossier]);

  useEffect(() => {
    const q = searchQuery.trim();
    const id = ++searchSeq.current;
    if (q.length < 2) {
      setSearchHits([]);
      setPickListTitle('');
      return undefined;
    }
    const timer = setTimeout(async () => {
      try {
        const res = await receptionService.searchMembers(q, { limit: 25 });
        if (id !== searchSeq.current) return;
        if (res.success) {
          setSearchHits(Array.isArray(res.data) ? res.data : []);
          setPickListTitle('');
        } else {
          setSearchHits([]);
        }
      } catch {
        if (id !== searchSeq.current) return;
        setSearchHits([]);
      }
    }, 380);
    return () => clearTimeout(timer);
  }, [searchQuery]);

  const selectMember = useCallback(
    (m) => {
      setMember(m);
      setSearchQuery(m.matricule ? String(m.matricule) : `${m.prenom || ''} ${m.nom || ''}`.trim());
      setSearchHits([]);
      setPickListTitle('');
      toast('success', D.TOAST_LOADED);
    },
    [toast]
  );

  const onSearchSubmit = async (ev) => {
    ev.preventDefault();
    const raw = searchQuery.trim();
    if (!raw) {
      toast('warning', D.TOAST_SEARCH_REQUIRED);
      return;
    }
    setSearchLoading(true);
    try {
      const res = await receptionService.lookupMemberByMatricule(raw);
      if (res.success) {
        const list = Array.isArray(res.data) ? res.data : [];
        if (list.length === 1) {
          selectMember(list[0]);
          return;
        }
        if (list.length > 1) {
          setSearchHits(list);
          setPickListTitle(D.MULTIPLE_MAT_TITLE);
          return;
        }
      }
      if (searchHits.length === 1) {
        selectMember(searchHits[0]);
        return;
      }
      if (searchHits.length > 1) {
        toast('warning', D.MULTIPLE_MAT_TITLE);
        return;
      }
      if (raw.length >= 2) {
        const nameRes = await receptionService.searchMembers(raw, { limit: 25 });
        const nameList = nameRes.success && Array.isArray(nameRes.data) ? nameRes.data : [];
        if (nameList.length === 1) {
          selectMember(nameList[0]);
          return;
        }
        if (nameList.length > 1) {
          setSearchHits(nameList);
          setPickListTitle('');
          toast('warning', D.MULTIPLE_MAT_TITLE);
          return;
        }
      }
      toast('warning', D.TOAST_NO_MEMBER);
    } catch (e) {
      toast('danger', e.message || D.TOAST_ERROR);
    } finally {
      setSearchLoading(false);
    }
  };

  const clearSelection = () => {
    setMember(null);
    setSearchQuery('');
    setSearchHits([]);
    setPickListTitle('');
  };

  const pageSubtitle = isDentist ? D.PAGE_SUBTITLE_DENTAL : D.PAGE_SUBTITLE_MEDICAL;

  const viewDossier = useMemo(
    () => filterDossierForWorkspace(fullDossier, clinicalWorkspace),
    [fullDossier, clinicalWorkspace]
  );

  const displayMember = viewDossier?.member || fullDossier?.member || member;
  const metaAge = displayMember && computeAgeYears(displayMember.date_naissance);

  const chronology = useMemo(() => buildChronology(viewDossier), [viewDossier]);

  const latestVitals = useMemo(() => latestVitalsSnapshot(viewDossier), [viewDossier]);

  const counts = useMemo(() => {
    if (!viewDossier) return null;
    return {
      consultations: viewDossier.consultations?.length ?? 0,
      visits: viewDossier.visits?.length ?? 0,
      prescriptions: viewDossier.prescriptions?.length ?? 0,
      rdv: viewDossier.rendezVous?.length ?? 0,
      nursingVitals: viewDossier.nursing?.vitals?.length ?? 0,
      nursingCare: viewDossier.nursing?.careActions?.length ?? 0,
      transfers: viewDossier.nursing?.transfers?.length ?? 0
    };
  }, [viewDossier]);

  const renderMemberHit = (m, onPick) => (
    <button key={m.id} type="button" className="dmn-hit-item" onClick={() => onPick(m)}>
      <span className="dmn-hit-item__avatar" aria-hidden>
        {(m.prenom?.[0] || '').toUpperCase()}
        {(m.nom?.[0] || '').toUpperCase()}
      </span>
      <span className="dmn-hit-item__body">
        <span className="dmn-hit-item__name">
          {m.prenom} {m.nom}
        </span>
        <span className="dmn-hit-item__meta">
          <span className="font-monospace">{m.matricule}</span>
          {m.company_name ? <span> · {m.company_name}</span> : null}
        </span>
      </span>
      <Icon name="arrowRight" size={16} className="dmn-hit-item__chevron" aria-hidden />
    </button>
  );

  const pickFromSearch = (m) => selectMember(m);

  const unifiedSearchForm = (
    <Form onSubmit={onSearchSubmit} className="dmn-search-unified">
      <Form.Label className={isHeadDoctor ? 'dmn-search-field__label' : 'small text-muted'}>
        {D.SEARCH_LABEL}
      </Form.Label>
      <div className={isHeadDoctor ? 'dmn-search-field__row' : 'd-flex flex-wrap gap-2'}>
        <div className={isHeadDoctor ? 'dmn-search-input-wrap' : 'flex-grow-1'}>
          {isHeadDoctor ? (
            <Icon name="search" size={18} className="dmn-search-input-wrap__icon" aria-hidden />
          ) : null}
          <Form.Control
            className={isHeadDoctor ? 'dmn-search-input' : undefined}
            value={searchQuery}
            onChange={(ev) => setSearchQuery(ev.target.value)}
            placeholder={D.SEARCH_PLACEHOLDER}
            autoComplete="off"
            aria-describedby="dmn-search-hint"
          />
        </div>
        <Button
          type="submit"
          variant={isHeadDoctor ? undefined : 'primary'}
          className={isHeadDoctor ? 'dmn-search-submit' : 'flex-shrink-0'}
          disabled={searchLoading}
        >
          {searchLoading ? <Spinner animation="border" size="sm" /> : D.SEARCH_SUBMIT}
        </Button>
      </div>
      <p id="dmn-search-hint" className={isHeadDoctor ? 'dmn-search-shell__desc mb-0 mt-2' : 'small text-muted mt-1 mb-0'}>
        {D.SEARCH_HINT}
      </p>
      {searchHits.length > 0 ? (
        <div className={isHeadDoctor ? 'dmn-pick-list' : 'mt-2'}>
          {pickListTitle ? <p className={isHeadDoctor ? 'dmn-pick-list__title' : 'small fw-semibold text-muted mb-2'}>{pickListTitle}</p> : null}
          {isHeadDoctor ? (
            <div className="dmn-hit-list">{searchHits.map((m) => renderMemberHit(m, pickFromSearch))}</div>
          ) : (
            <ListGroup variant="flush" className="border rounded overflow-auto" style={{ maxHeight: 240 }}>
              {searchHits.map((m) => (
                <ListGroup.Item
                  key={m.id}
                  action
                  as="button"
                  type="button"
                  className="py-2 text-start small"
                  onClick={() => pickFromSearch(m)}
                >
                  <span className="fw-semibold">
                    {m.prenom} {m.nom}
                  </span>
                  <span className="text-muted ms-2 font-monospace">{m.matricule}</span>
                  {m.company_name ? <span className="text-muted ms-2">· {m.company_name}</span> : null}
                </ListGroup.Item>
              ))}
            </ListGroup>
          )}
        </div>
      ) : null}
    </Form>
  );

  const searchPanel = isHeadDoctor ? (
    <section className="dmn-search-shell dmn-search-panel recv-no-print">
      <header className="dmn-search-shell__head">
        <div className="dmn-search-shell__icon" aria-hidden>
          <Icon name="search" size={20} />
        </div>
        <div>
          <h2 className="dmn-search-shell__title">{D.SEARCH_CARD_TITLE}</h2>
        </div>
      </header>
      <div className="dmn-search-shell__body">{unifiedSearchForm}</div>
    </section>
  ) : (
    <Card className="shadow-sm recv-panel-card mb-4 dmn-search-panel recv-no-print">
      <Card.Header className="bg-white fw-semibold">{D.SEARCH_CARD_TITLE}</Card.Header>
      <Card.Body>{unifiedSearchForm}</Card.Body>
    </Card>
  );

  return (
    <div className={`doctor-dashboard recv-dash-pro--main${isHeadDoctor ? ' dmn-page dmn-page--hd' : ''}`}>
      {isHeadDoctor ? (
        <HeadDoctorPageHeader
          badge={HEAD_DOCTOR_COPY.DASHBOARD.BADGE}
          title={HEAD_DOCTOR_COPY.NAV.dossier.label}
          description={pageSubtitle}
        />
      ) : (
        <div className="mb-4">
          <h1 className="h3 mb-1">{D.PAGE_TITLE}</h1>
          <p className="text-muted mb-0 small">{pageSubtitle}</p>
        </div>
      )}

      {searchPanel}

      {!member ? (
        isHeadDoctor ? (
          <div className="dmn-empty-state">
            <div className="dmn-empty-state__icon" aria-hidden>
              <Icon name="user" size={28} />
            </div>
            <h2 className="dmn-empty-state__title">Aucun adhérent sélectionné</h2>
            <p className="dmn-empty-state__text">{D.EMPTY_STATE}</p>
          </div>
        ) : (
          <Card className="shadow-sm border-0 bg-light">
            <Card.Body className="text-muted small">{D.EMPTY_STATE}</Card.Body>
          </Card>
        )
      ) : (
        <div className="dmn-root">
          <div className={`dmn-fiche-header position-relative${isHeadDoctor ? ' dmn-fiche-header--hd' : ''}`}>
            <div className="recv-no-print dmn-fiche-header__actions">
              <Button
                variant={isHeadDoctor ? 'outline-light' : 'light'}
                size="sm"
                className={isHeadDoctor ? 'dmn-fiche-header__clear' : 'border-0 shadow-sm text-dark'}
                onClick={clearSelection}
              >
                {isHeadDoctor ? <Icon name="times" size={14} className="me-1" aria-hidden /> : null}
                {D.CLEAR_MEMBER}
              </Button>
            </div>
            <div className="dmn-fiche-header__label">
              {isHeadDoctor ? 'Dossier médical consolidé · SMIE' : 'Dossier médical numérique consolidé · SMIE'}
            </div>
            <div className="dmn-fiche-header__name">
              {displayMember.prenom} {displayMember.nom}
            </div>
            <div className="dmn-fiche-header__chips">
              <span className="dmn-fiche-header__chip">
                <kbd>Mat.</kbd> <span className="font-monospace">{displayMember.matricule || '—'}</span>
              </span>
              {displayMember.company_name ? (
                <span className="dmn-fiche-header__chip">{displayMember.company_name}</span>
              ) : null}
              <span className="dmn-fiche-header__chip">
                {displayMember.sexe === 'M' ? D.SEXE_M : displayMember.sexe === 'F' ? D.SEXE_F : 'Sexe —'}
                {metaAge != null ? ` · ${metaAge} ans` : ''}
              </span>
              {displayMember.date_naissance && !String(displayMember.date_naissance).startsWith('0000') ? (
                <span className="dmn-fiche-header__chip">Né(e) {displayMember.date_naissance}</span>
              ) : null}
              {displayMember.fonction ? (
                <span className="dmn-fiche-header__chip">{displayMember.fonction}</span>
              ) : null}
            </div>
            <div className="dmn-fiche-header__footer">
              Consultation du dossier :{' '}
              {new Date().toLocaleString('fr-FR', { dateStyle: 'long', timeStyle: 'short' })} — usage professionnel.
            </div>
          </div>

          {dossierLoading ? (
            <div className={`dmn-loading${isHeadDoctor ? ' dmn-loading--hd' : ''}`}>
              <Spinner animation="border" variant="primary" />
              <p className="dmn-loading__text">Chargement du dossier médical…</p>
            </div>
          ) : viewDossier ? (
            <>
              {fullDossier?.consultationsTruncated ? (
                <Alert variant="info" className="py-2 small">
                  L’historique des consultations est tronqué ({viewDossier.consultations?.length ?? 0} affichées /{' '}
                  {fullDossier.consultationsTotal} au total). Contactez l’administrateur pour export complet si
                  nécessaire.
                </Alert>
              ) : null}

              <div className="dmn-print-actions recv-no-print">
                <Button
                  variant="outline-primary"
                  size="sm"
                  className={isHeadDoctor ? 'dmn-print-btn' : ''}
                  onClick={() => window.print()}
                >
                  {isHeadDoctor ? <Icon name="download" size={16} className="me-1" aria-hidden /> : null}
                  Imprimer le dossier
                </Button>
              </div>

              <nav
                className={`dmn-sommaire recv-no-print${isHeadDoctor ? ' dmn-sommaire--hd' : ''}`}
                aria-label="Sommaire du dossier"
              >
                <div className="dmn-sommaire__title">Sommaire</div>
                <div className="dmn-sommaire__links">
                  {[
                    ['dmn-identite', 'Identité'],
                    ['dmn-synthese', 'Synthèse'],
                    ['dmn-chrono', 'Chronologie'],
                    ['dmn-consult', 'Consultations'],
                    ['dmn-rx', 'Ordonnances'],
                    ['dmn-nursing', 'Infirmerie'],
                    ['dmn-visits', 'Accueil'],
                    ['dmn-rdv', 'Rendez-vous']
                  ].map(([id, label]) => (
                    <a key={id} href={`#${id}`} className="dmn-sommaire__pill">
                      {label}
                    </a>
                  ))}
                </div>
              </nav>

              <section id="dmn-identite" className="dmn-section">
                <header className="dmn-section__head">
                  <span className="dmn-section__num">1</span>
                  <div>
                    <h2 className="dmn-section__title">Identité administrative & liens</h2>
                    <p className="dmn-section__subtitle mb-0">Données adhérent (SMIE)</p>
                  </div>
                </header>
                <div className="dmn-section__body">
                  <Row className="gy-2 small">
                    <Col xs={12} md={6}>
                      <span className="text-muted">{D.LABEL_SOCIETY} </span>
                      {displayMember.company_name || '—'}
                    </Col>
                    <Col xs={6} md={3}>
                      <span className="text-muted">{D.LABEL_SEXE} </span>
                      {displayMember.sexe === 'M' ? D.SEXE_M : displayMember.sexe === 'F' ? D.SEXE_F : '—'}
                    </Col>
                    <Col xs={6} md={3}>
                      <span className="text-muted">{D.LABEL_NAIS} </span>
                      {displayMember.date_naissance && !String(displayMember.date_naissance).startsWith('0000')
                        ? displayMember.date_naissance
                        : '—'}
                      {metaAge != null ? ` (${metaAge} ans)` : ''}
                    </Col>
                    <Col xs={12} md={6}>
                      <span className="text-muted">{D.LABEL_FONCTION} </span>
                      {displayMember.fonction || '—'}
                    </Col>
                    {displayMember.agent_matricule ? (
                      <Col xs={12}>
                        <span className="text-muted">Ayant droit de : </span>
                        {displayMember.agent_prenom} {displayMember.agent_nom}{' '}
                        <span className="font-monospace text-muted">({displayMember.agent_matricule})</span>
                      </Col>
                    ) : null}
                  </Row>
                </div>
              </section>

              <section id="dmn-synthese" className="dmn-section">
                <header className="dmn-section__head">
                  <span className="dmn-section__num">2</span>
                  <div>
                    <h2 className="dmn-section__title">Synthèse d’activité & constantes récentes</h2>
                    <p className="dmn-section__subtitle mb-0">Volumes enregistrés dans le périmètre SMIE</p>
                  </div>
                </header>
                <div className="dmn-section__body">
                  {counts ? (
                    <div className={`dmn-synthese-grid${isHeadDoctor ? ' dmn-synthese-grid--hd' : ''}`}>
                      {[
                        { label: 'Consultations', value: counts.consultations, icon: 'stethoscope' },
                        { label: 'Ordonnances', value: counts.prescriptions, icon: 'pills' },
                        { label: 'Accueil', value: counts.visits, icon: 'inbox' },
                        { label: 'Rendez-vous', value: counts.rdv, icon: 'calendar' },
                        {
                          label: 'Infirmerie',
                          value:
                            counts.nursingVitals + counts.nursingCare + counts.transfers > 0
                              ? counts.nursingVitals + counts.nursingCare + counts.transfers
                              : '—',
                          icon: 'heart'
                        }
                      ].map(({ label, value, icon }) => (
                        <div key={label} className="dmn-synthese-kpi">
                          {isHeadDoctor ? (
                            <span className="dmn-synthese-kpi__icon" aria-hidden>
                              <Icon name={icon} size={16} />
                            </span>
                          ) : null}
                          <div className="label">{label}</div>
                          <div className="value">{value}</div>
                        </div>
                      ))}
                    </div>
                  ) : null}
                  {latestVitals ? (
                    <div className="mt-3">
                      <div className="small fw-semibold text-muted mb-1">
                        Dernières constantes connues ({latestVitals.source})
                      </div>
                      <div className="dmn-nd">
                        <strong>{latestVitals.text}</strong>
                        <span className="text-muted"> · {formatWhen(latestVitals.at)}</span>
                        {latestVitals.note ? (
                          <>
                            <br />
                            <span className="small">{latestVitals.note}</span>
                          </>
                        ) : null}
                      </div>
                    </div>
                  ) : (
                    <div className="dmn-nd mt-3">Aucune constante récente centralisée pour cet adhérent.</div>
                  )}
                  <p className="text-muted small mt-3 mb-0">{D.VITALS_HINT}</p>
                </div>
              </section>

              <section id="dmn-chrono" className="dmn-section">
                <header className="dmn-section__head">
                  <span className="dmn-section__num">3</span>
                  <div>
                    <h2 className="dmn-section__title">Frise chronologique</h2>
                    <p className="dmn-section__subtitle mb-0">
                      Derniers événements (consultations, accueil, ordonnances, soins, rendez-vous)
                    </p>
                  </div>
                </header>
                <div className="dmn-section__body">
                  {chronology.length === 0 ? (
                    <p className="text-muted small mb-0">Aucun événement dans le dossier.</p>
                  ) : (
                    <ul className="dmn-timeline list-unstyled mb-0">
                      {chronology.map((ev) => {
                        const bodyEx = excerpt(ev.body, 220);
                        return (
                          <li key={ev.key} className="dmn-timeline__item">
                            <span className="dmn-timeline__dot" aria-hidden />
                            <div className="dmn-timeline__card">
                              <div className="d-flex flex-wrap gap-2 align-items-start justify-content-between">
                                <div>
                                  <Badge bg={CHRONO_BADGE[ev.kind] || 'secondary'} className="me-2 text-uppercase">
                                    {ev.kind}
                                  </Badge>
                                  <span className="fw-semibold">{ev.title}</span>
                                  {ev.subtitle ? (
                                    <span className="text-muted small ms-2">{ev.subtitle}</span>
                                  ) : null}
                                </div>
                                <span className="small text-muted text-nowrap">{formatWhen(ev.at)}</span>
                              </div>
                              {bodyEx.text ? (
                                <div className="small mt-2 mb-0" style={{ whiteSpace: 'pre-wrap' }}>
                                  {bodyEx.text}
                                  {bodyEx.more ? '…' : ''}
                                </div>
                              ) : null}
                            </div>
                          </li>
                        );
                      })}
                    </ul>
                  )}
                </div>
              </section>

              <section id="dmn-consult" className="dmn-section">
                <header className="dmn-section__head">
                  <span className="dmn-section__num">4</span>
                  <div>
                    <h2 className="dmn-section__title">Consultations cliniques</h2>
                    <p className="dmn-section__subtitle mb-0">
                      {isDentist ? D.CONSULT_SECTION_DENTAL : D.CONSULT_SECTION}
                    </p>
                  </div>
                </header>
                <div className="dmn-section__body">
                  {!viewDossier.consultations?.length ? (
                    <p className="text-muted small mb-0">
                      {isDentist ? D.CONSULT_EMPTY_DENTAL : D.CONSULT_EMPTY}
                    </p>
                  ) : (
                    <div className="dmn-table-wrap table-responsive bg-white">
                      <Table hover size="sm" className="mb-0 align-middle">
                              <thead className="table-light">
                                <tr>
                                  <th>Date</th>
                                  <th>Site</th>
                                  <th>Statut</th>
                                  <th>Filière</th>
                                  <th>Statut infirmier</th>
                                  <th>Professionnel</th>
                                  <th>Constantes</th>
                                  <th>Clinique</th>
                                  <th>Ouvrir</th>
                                </tr>
                              </thead>
                              <tbody>
                                {viewDossier.consultations.map((c) => {
                                  const line = (c.service_line || 'MEDICAL').toUpperCase();
                                  const badge = SERVICE_LINE_BADGE[line] || SERVICE_LINE_BADGE.MEDICAL;
                                  const st =
                                    c.status === 'COMPLETED'
                                      ? { bg: 'success', label: D.STATUS_DONE }
                                      : c.status === 'PENDING'
                                        ? { bg: 'warning', text: 'dark', label: D.STATUS_PENDING }
                                        : { bg: 'secondary', label: D.STATUS_CANCEL };
                                  const symEx = excerpt(c.symptoms, 100);
                                  const diaEx = excerpt(c.diagnosis, 100);
                                  return (
                                    <tr key={c.id}>
                                      <td className="text-nowrap">{formatWhen(c.consultation_date)}</td>
                                      <td>{c.site_name || '—'}</td>
                                      <td>
                                        <Badge bg={st.bg} text={st.text}>
                                          {st.label}
                                        </Badge>
                                      </td>
                                      <td>
                                        <Badge bg={badge.bg}>{badge.label}</Badge>
                                      </td>
                                      <td className="small text-muted">
                                        {c.nursing_status ? (
                                          <Badge bg="light" text="dark" className="border fw-normal">
                                            {String(c.nursing_status).replace(/_/g, ' ')}
                                          </Badge>
                                        ) : (
                                          '—'
                                        )}
                                      </td>
                                      <td>{c.doctor_name || '—'}</td>
                                      <td className="small text-nowrap">{vitalsSummary(c)}</td>
                                      <td className="small">
                                        {symEx.text ? (
                                          <span>
                                            <strong>S:</strong> {symEx.text}
                                            {symEx.more ? '…' : ''}
                                          </span>
                                        ) : null}{' '}
                                        {diaEx.text ? (
                                          <span>
                                            <strong>D:</strong> {diaEx.text}
                                            {diaEx.more ? '…' : ''}
                                          </span>
                                        ) : null}
                                        {!symEx.text && !diaEx.text ? '—' : null}
                                      </td>
                                      <td>
                                        <Link to={doctorConsultationEditPath(c.id, portalPaths)} className="small">
                                          Fiche
                                        </Link>
                                      </td>
                                    </tr>
                                  );
                                })}
                              </tbody>
                            </Table>
                    </div>
                  )}
                </div>
              </section>

              <section id="dmn-rx" className="dmn-section">
                <header className="dmn-section__head">
                  <span className="dmn-section__num">5</span>
                  <div>
                    <h2 className="dmn-section__title">Ordonnances</h2>
                    <p className="dmn-section__subtitle mb-0">Prescriptions liées aux consultations SMIE</p>
                  </div>
                </header>
                <div className="dmn-section__body">
                  {!viewDossier.prescriptions?.length ? (
                    <p className="text-muted small mb-0">Aucune ordonnance liée aux consultations SMIE.</p>
                  ) : (
                    <div className="dmn-table-wrap table-responsive bg-white">
                      <Table hover size="sm" className="mb-0 align-middle">
                              <thead className="table-light">
                                <tr>
                                  <th>Date</th>
                                  <th>Statut</th>
                                  <th>Médecin</th>
                                  <th>Site</th>
                                  <th>Consult.</th>
                                  <th>Lignes</th>
                                </tr>
                              </thead>
                              <tbody>
                                {viewDossier.prescriptions.map((p) => (
                                  <tr key={p.id}>
                                    <td className="text-nowrap">{formatWhen(p.prescribed_date)}</td>
                                    <td>
                                      <Badge bg={p.status === 'ACTIVE' ? 'success' : 'secondary'}>{p.status}</Badge>
                                    </td>
                                    <td>{p.doctor_name || '—'}</td>
                                    <td>{p.site_name || '—'}</td>
                                    <td>
                                      {p.consultation_id ? (
                                        <Link
                                          to={doctorConsultationEditPath(p.consultation_id, portalPaths)}
                                          className="small"
                                        >
                                          #{p.consultation_id}
                                        </Link>
                                      ) : (
                                        '—'
                                      )}
                                    </td>
                                    <td className="small">
                                      {(p.items || [])
                                        .map(
                                          (it) =>
                                            `${it.medication_name || it.name || 'Méd.'} × ${it.quantity}${it.dosage ? ` (${it.dosage})` : ''}`
                                        )
                                        .join(' · ') || '—'}
                                    </td>
                                  </tr>
                                ))}
                              </tbody>
                            </Table>
                    </div>
                  )}
                </div>
              </section>

              <section id="dmn-nursing" className="dmn-section">
                <header className="dmn-section__head">
                  <span className="dmn-section__num">6</span>
                  <div>
                    <h2 className="dmn-section__title">Suivi infirmier</h2>
                    <p className="dmn-section__subtitle mb-0">
                      Constantes, soins et transferts pour les consultations de cet adhérent
                    </p>
                  </div>
                </header>
                <div className="dmn-section__body">
                        <h6 className="small fw-semibold mt-0 mb-2">Constantes (historique)</h6>
                        {!viewDossier.nursing?.vitals?.length ? (
                          <p className="text-muted small">Aucune entrée.</p>
                        ) : (
                          <Table size="sm" bordered responsive className="bg-white small">
                            <thead className="table-light">
                              <tr>
                                <th>Date</th>
                                <th># Consult.</th>
                                <th>TA / FC / T°</th>
                                <th>Note</th>
                                <th>Par</th>
                              </tr>
                            </thead>
                            <tbody>
                              {viewDossier.nursing.vitals.map((v) => (
                                <tr key={`v-${v.id}`}>
                                  <td className="text-nowrap">{formatWhen(v.created_at)}</td>
                                  <td>
                                    <Link
                                      to={doctorConsultationEditPath(v.consultation_id, portalPaths)}
                                      className="small"
                                    >
                                      {v.consultation_id}
                                    </Link>
                                  </td>
                                  <td>
                                    {v.bp_systolic != null && v.bp_diastolic != null
                                      ? `${v.bp_systolic}/${v.bp_diastolic}`
                                      : '—'}{' '}
                                    · FC {v.heart_rate_bpm ?? '—'} · T° {v.temperature_c ?? '—'}
                                  </td>
                                  <td>{v.note || '—'}</td>
                                  <td>
                                    {v.actor_first_name || ''} {v.actor_last_name || ''}
                                  </td>
                                </tr>
                              ))}
                            </tbody>
                          </Table>
                        )}
                        <h6 className="small fw-semibold mt-3">Actions de soin</h6>
                        {!viewDossier.nursing?.careActions?.length ? (
                          <p className="text-muted small">Aucune entrée.</p>
                        ) : (
                          <Table size="sm" bordered responsive className="bg-white small">
                            <thead className="table-light">
                              <tr>
                                <th>Date</th>
                                <th># Consult.</th>
                                <th>Type</th>
                                <th>Description</th>
                                <th>Par</th>
                              </tr>
                            </thead>
                            <tbody>
                              {viewDossier.nursing.careActions.map((a) => (
                                <tr key={`a-${a.id}`}>
                                  <td className="text-nowrap">{formatWhen(a.created_at)}</td>
                                  <td>
                                    <Link
                                      to={doctorConsultationEditPath(a.consultation_id, portalPaths)}
                                      className="small"
                                    >
                                      {a.consultation_id}
                                    </Link>
                                  </td>
                                  <td>{a.action_type}</td>
                                  <td>{a.description}</td>
                                  <td>
                                    {a.actor_first_name || ''} {a.actor_last_name || ''}
                                  </td>
                                </tr>
                              ))}
                            </tbody>
                          </Table>
                        )}
                        <h6 className="small fw-semibold mt-3">Transferts de filière</h6>
                        {!viewDossier.nursing?.transfers?.length ? (
                          <p className="text-muted small">Aucune entrée.</p>
                        ) : (
                          <Table size="sm" bordered responsive className="bg-white small">
                            <thead className="table-light">
                              <tr>
                                <th>Date</th>
                                <th># Consult.</th>
                                <th>De → vers</th>
                                <th>Motif</th>
                                <th>Par</th>
                              </tr>
                            </thead>
                            <tbody>
                              {viewDossier.nursing.transfers.map((t) => (
                                <tr key={`t-${t.id}`}>
                                  <td className="text-nowrap">{formatWhen(t.created_at)}</td>
                                  <td>
                                    <Link
                                      to={doctorConsultationEditPath(t.consultation_id, portalPaths)}
                                      className="small"
                                    >
                                      {t.consultation_id}
                                    </Link>
                                  </td>
                                  <td>
                                    {t.from_service_line} → {t.to_service_line}
                                  </td>
                                  <td>{t.reason}</td>
                                  <td>
                                    {t.actor_first_name || ''} {t.actor_last_name || ''}
                                  </td>
                                </tr>
                              ))}
                            </tbody>
                          </Table>
                        )}
                </div>
              </section>

              <section id="dmn-visits" className="dmn-section">
                <header className="dmn-section__head">
                  <span className="dmn-section__num">7</span>
                  <div>
                    <h2 className="dmn-section__title">{D.VISITS_SECTION}</h2>
                    <p className="dmn-section__subtitle mb-0">Historique des passages accueil</p>
                  </div>
                </header>
                <div className="dmn-section__body">
                        {!viewDossier.visits?.length ? (
                          <p className="text-muted small mb-0">{D.VISITS_EMPTY}</p>
                        ) : (
                          <div className="recv-journal-list-wrap border rounded bg-white">
                            <div className="recv-journal-list" role="list" aria-label="Historique des passages">
                              {VISIT_LIST_HEAD}
                              <VisitHistoryTableRows rows={viewDossier.visits} />
                            </div>
                          </div>
                        )}
                </div>
              </section>

              <section id="dmn-rdv" className="dmn-section">
                <header className="dmn-section__head">
                  <span className="dmn-section__num">8</span>
                  <div>
                    <h2 className="dmn-section__title">Rendez-vous</h2>
                    <p className="dmn-section__subtitle mb-0">Planification et statuts</p>
                  </div>
                </header>
                <div className="dmn-section__body">
                        {!viewDossier.rendezVous?.length ? (
                          <p className="text-muted small mb-0">Aucun rendez-vous enregistré pour cet adhérent.</p>
                        ) : (
                          <div className="dmn-table-wrap table-responsive bg-white">
                            <Table hover size="sm" className="mb-0 align-middle">
                              <thead className="table-light">
                                <tr>
                                  <th>Début</th>
                                  <th>Réf.</th>
                                  <th>Type</th>
                                  <th>Service</th>
                                  <th>Médecin</th>
                                  <th>Statut</th>
                                  <th>Motif</th>
                                </tr>
                              </thead>
                              <tbody>
                                {viewDossier.rendezVous.map((r) => (
                                  <tr key={r.id}>
                                    <td className="text-nowrap">{formatWhen(r.start_at)}</td>
                                    <td className="font-monospace small">{r.reference}</td>
                                    <td>{r.type_rendez_vous || '—'}</td>
                                    <td className="small">{r.service || '—'}</td>
                                    <td className="small">
                                      {r.doctor_first_name || ''} {r.doctor_last_name || ''}
                                    </td>
                                    <td>
                                      <Badge bg="secondary" className="fw-normal">
                                        {RDV_STATUS_LABEL[r.status] || r.status}
                                      </Badge>
                                    </td>
                                    <td className="small">{excerpt(r.motif, 80).text || '—'}</td>
                                  </tr>
                                ))}
                              </tbody>
                            </Table>
                          </div>
                        )}
                </div>
              </section>
            </>
          ) : (
            <Alert variant="warning" className="small mb-0">
              Dossier non chargé. Vérifiez votre connexion et réessayez.
            </Alert>
          )}
        </div>
      )}
    </div>
  );
}
